import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { paths } from "@/types/api";

type SubscriberBody =
  paths["/api/v1/subscribers"]["post"]["requestBody"]["content"]["application/json"];

export const subscriberKeys = { all: ["subscribers"] as const };

export function useSubscribers() {
  return useQuery({
    queryKey: subscriberKeys.all,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/subscribers", {});
      if (error || !data) throw new Error("Failed to load subscribers");
      return data;
    },
  });
}

export function useCreateSubscriber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SubscriberBody) => {
      const { data, error } = await apiClient.POST("/api/v1/subscribers", { body });
      if (error || !data) throw new Error("Failed to create subscriber");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: subscriberKeys.all }),
  });
}

export function useResendConfirmation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/subscribers/{subscriber_id}/resend-confirmation",
        { params: { path: { subscriber_id: id } } },
      );
      if (error || !data) throw new Error("Could not resend confirmation");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: subscriberKeys.all }),
  });
}

export function useUnsubscribeSubscriber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/subscribers/{subscriber_id}/unsubscribe",
        { params: { path: { subscriber_id: id } } },
      );
      if (error || !data) throw new Error("Could not unsubscribe");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: subscriberKeys.all }),
  });
}

export function useRunMatching() {
  return useMutation({
    mutationFn: async (propertyId: string) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/subscribers/match/{property_id}",
        { params: { path: { property_id: propertyId } } },
      );
      if (error || !data) throw new Error("Matching failed");
      return data;
    },
  });
}
